/**
 * What the recorded body measurements and blood pressure imply.
 *
 * Height, weight, waist and the two blood-pressure numbers are measurements:
 * somebody read them off an instrument, and they are stored as results with
 * a flag and an attempt history. Everything in this file is arithmetic on
 * those readings — a BMI, a basal metabolic rate, a mean arterial pressure —
 * and is recomputed from the stored readings wherever it is shown. Nothing
 * here is a diagnosis, and the fitness decision does not rest on any of it.
 *
 * BMI is the one derived figure that is also stored, because protocols list
 * it as a test and a reference range is printed for it. The encounter
 * service records it from height and weight (see `deriveBmi`); this is the
 * formula it uses, so the station preview and the stored value agree.
 *
 * Dependency-free on purpose: the SaaS imports this folder through the
 * `@dru/clinical-math` alias, so the number a technician sees while typing
 * is the number the API stores and the report prints. Keep it that way —
 * no Nest, no Prisma, no DOM.
 */

/** The catalogue codes these formulas read. */
export const VITAL_CODE = {
  HEIGHT: 'VIT-HEIGHT',
  WEIGHT: 'VIT-WEIGHT',
  WAIST: 'VIT-WAIST',
  BMI: 'VIT-BMI',
  SYSTOLIC: 'VIT-BP-SYS',
  DIASTOLIC: 'VIT-BP-DIA',
  PULSE: 'VIT-PULSE',
  TEMPERATURE: 'VIT-TEMP',
  SPO2: 'VIT-SPO2',
} as const;

export type BmiBandCode = 'UNDERWEIGHT' | 'NORMAL' | 'OVERWEIGHT' | 'OBESE';
export type BloodPressureBandCode = 'NORMAL' | 'ELEVATED' | 'STAGE_1' | 'STAGE_2' | 'CRISIS';

export interface VitalInputs {
  heightCm?: number | null;
  weightKg?: number | null;
  waistCm?: number | null;
  systolic?: number | null;
  diastolic?: number | null;
  /** 'MALE' | 'FEMALE' | anything else (treated as not stated). */
  sex?: string | null;
  ageYears?: number | null;
}

export interface DerivedBody {
  bmi: number | null;
  bmiBand: { code: BmiBandCode; label: string } | null;
  /** Mifflin–St Jeor resting energy, kcal/day. Needs sex and age. */
  bmrKcalPerDay: number | null;
  /** Devine ideal body weight for the recorded height. Needs sex. */
  idealWeightKg: number | null;
  /** Mosteller body surface area, m². */
  bodySurfaceM2: number | null;
  /** 35 ml per kg of body weight, L/day. */
  waterLitresPerDay: number | null;
  /** A general activity target; shown beside the body figures. */
  stepTargetPerDay: number | null;
  /** 70–85% of the age-predicted maximum (220 − age), beats/min. */
  targetHeartRate: { low: number; high: number } | null;
}

export interface DerivedBloodPressure {
  /** (systolic + 2 × diastolic) / 3, mmHg. */
  meanArterialPressure: number | null;
  /** systolic − diastolic, mmHg. */
  pulsePressure: number | null;
  /** ACC/AHA 2017 band for this one reading. */
  band: { code: BloodPressureBandCode; label: string; note: string } | null;
}

export interface DerivedVitals {
  body: DerivedBody;
  bloodPressure: DerivedBloodPressure;
  /** True when at least one figure could be worked out. */
  any: boolean;
}

const num = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;
const round = (v: number, places: number): number => {
  const f = 10 ** places;
  return Math.round(v * f) / f;
};
const sexOf = (sex: string | null | undefined): 'MALE' | 'FEMALE' | null =>
  sex === 'MALE' || sex === 'FEMALE' ? sex : null;

/** Body mass index to one decimal, or null when either reading is missing or not positive. */
export function bmi(heightCm: number | null | undefined, weightKg: number | null | undefined): number | null {
  const h = num(heightCm), w = num(weightKg);
  if (h == null || w == null || h <= 0 || w <= 0) return null;
  const m = h / 100;
  return round(w / (m * m), 1);
}

/**
 * WHO bands. India's own guidance draws overweight at 23 and obesity at 25
 * for its population; the printed band says which scale it uses so a reader
 * is not misled by the word "normal".
 */
export function bmiBand(value: number | null | undefined): DerivedBody['bmiBand'] {
  const v = num(value);
  if (v == null) return null;
  if (v < 18.5) return { code: 'UNDERWEIGHT', label: 'Underweight (WHO)' };
  if (v < 25) return { code: 'NORMAL', label: 'Normal (WHO)' };
  if (v < 30) return { code: 'OVERWEIGHT', label: 'Overweight (WHO)' };
  return { code: 'OBESE', label: 'Obese (WHO)' };
}

/** Mifflin–St Jeor, kcal/day. Null without sex and age: the formula has no honest default for either. */
export function basalMetabolicRate(
  weightKg: number | null | undefined, heightCm: number | null | undefined,
  ageYears: number | null | undefined, sex: string | null | undefined,
): number | null {
  const w = num(weightKg), h = num(heightCm), a = num(ageYears), s = sexOf(sex);
  if (w == null || h == null || a == null || !s || w <= 0 || h <= 0 || a < 0) return null;
  return round(10 * w + 6.25 * h - 5 * a + (s === 'MALE' ? 5 : -161), 2);
}

/** Devine, kg. Below 152.4 cm the formula runs negative-ish and is not quoted. */
export function idealWeight(heightCm: number | null | undefined, sex: string | null | undefined): number | null {
  const h = num(heightCm), s = sexOf(sex);
  if (h == null || !s || h < 152.4) return null;
  return round((s === 'MALE' ? 50 : 45.5) + 0.9 * (h - 152.4), 1);
}

/** Mosteller, m². */
export function bodySurfaceArea(heightCm: number | null | undefined, weightKg: number | null | undefined): number | null {
  const h = num(heightCm), w = num(weightKg);
  if (h == null || w == null || h <= 0 || w <= 0) return null;
  return round(Math.sqrt((h * w) / 3600), 2);
}

/** 35 ml per kg, L/day. */
export function dailyWater(weightKg: number | null | undefined): number | null {
  const w = num(weightKg);
  if (w == null || w <= 0) return null;
  return round(w * 0.035, 2);
}

/** 70–85% of (220 − age), the moderate-to-vigorous exercise zone. */
export function targetHeartRate(ageYears: number | null | undefined): { low: number; high: number } | null {
  const a = num(ageYears);
  if (a == null || a < 1 || a > 120) return null;
  const max = 220 - a;
  return { low: Math.round(max * 0.7), high: Math.round(max * 0.85) };
}

export function meanArterialPressure(systolic: number | null | undefined, diastolic: number | null | undefined): number | null {
  const s = num(systolic), d = num(diastolic);
  if (s == null || d == null || s <= 0 || d <= 0 || d > s) return null;
  return round((s + 2 * d) / 3, 1);
}

export function pulsePressure(systolic: number | null | undefined, diastolic: number | null | undefined): number | null {
  const s = num(systolic), d = num(diastolic);
  if (s == null || d == null || s <= 0 || d <= 0 || d > s) return null;
  return s - d;
}

/**
 * ACC/AHA 2017 bands for a single reading. The notes describe the band;
 * they do not prescribe. One reading on one day is not a diagnosis, and the
 * band says so where it matters.
 */
export function bloodPressureBand(
  systolic: number | null | undefined, diastolic: number | null | undefined,
): DerivedBloodPressure['band'] {
  const s = num(systolic), d = num(diastolic);
  if (s == null || d == null || s <= 0 || d <= 0) return null;
  if (s > 180 || d > 120) {
    return { code: 'CRISIS', label: 'Hypertensive crisis range', note: 'A reading this high is rechecked at once and seen by the physician the same day.' };
  }
  if (s >= 140 || d >= 90) {
    return { code: 'STAGE_2', label: 'Stage 2 hypertension range', note: 'Usually confirmed on a second occasion before anything is decided; the physician reads it with the rest of the record.' };
  }
  if (s >= 130 || d >= 80) {
    return { code: 'STAGE_1', label: 'Stage 1 hypertension range', note: 'One reading in this band is common after exertion, caffeine or a rushed walk to the station; it is confirmed on another day before it means anything.' };
  }
  if (s >= 120) {
    return { code: 'ELEVATED', label: 'Elevated', note: 'Above the normal band on the upper number only. Worth a repeat at the next visit.' };
  }
  return { code: 'NORMAL', label: 'Normal', note: 'Both numbers within the normal band for this reading.' };
}

export function derivedVitals(i: VitalInputs): DerivedVitals {
  const h = num(i.heightCm), w = num(i.weightKg);
  const bmiValue = bmi(h, w);
  const body: DerivedBody = {
    bmi: bmiValue,
    bmiBand: bmiBand(bmiValue),
    bmrKcalPerDay: basalMetabolicRate(w, h, i.ageYears, i.sex),
    idealWeightKg: idealWeight(h, i.sex),
    bodySurfaceM2: bodySurfaceArea(h, w),
    waterLitresPerDay: dailyWater(w),
    stepTargetPerDay: bmiValue != null ? 10_000 : null,
    targetHeartRate: bmiValue != null ? targetHeartRate(i.ageYears) : null,
  };
  const bloodPressure: DerivedBloodPressure = {
    meanArterialPressure: meanArterialPressure(i.systolic, i.diastolic),
    pulsePressure: pulsePressure(i.systolic, i.diastolic),
    band: bloodPressureBand(i.systolic, i.diastolic),
  };
  const any = Object.values(body).some((v) => v != null) || Object.values(bloodPressure).some((v) => v != null);
  return { body, bloodPressure, any };
}

/**
 * The same, read off a map of catalogue code → numeric value — what an
 * encounter's results or a station's typed values reduce to.
 */
export function derivedFromCodes(
  values: ReadonlyMap<string, number | null | undefined> | Record<string, number | null | undefined>,
  who: { sex?: string | null; ageYears?: number | null } = {},
): DerivedVitals {
  const get = (code: string) => values instanceof Map ? values.get(code) : (values as Record<string, number | null | undefined>)[code];
  return derivedVitals({
    heightCm: get(VITAL_CODE.HEIGHT), weightKg: get(VITAL_CODE.WEIGHT), waistCm: get(VITAL_CODE.WAIST),
    systolic: get(VITAL_CODE.SYSTOLIC), diastolic: get(VITAL_CODE.DIASTOLIC),
    sex: who.sex ?? null, ageYears: who.ageYears ?? null,
  });
}

/** Age in whole years at a date, from a date of birth in any parseable form. */
export function ageInYears(dateOfBirth: string | Date | null | undefined, at: Date = new Date()): number | null {
  if (!dateOfBirth) return null;
  const dob = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  let years = at.getFullYear() - dob.getFullYear();
  const m = at.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && at.getDate() < dob.getDate())) years--;
  return years;
}
